import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Upload, FileText } from "lucide-react";
import toast from "react-hot-toast";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
  fontSize: 13.5, color: "#111827", outline: "none",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };

const DOC_TYPE_LABELS = {
  rc:        "RC (Registration Certificate)",
  insurance: "Insurance",
  permit:    "Permit",
  puc:       "PUC (Pollution Certificate)",
  fitness:   "Fitness Certificate",
};

export default function DocumentFormModal({ document, vehicles, defaultVehicleId, onClose, onSubmit }) {
  const isEdit = !!document;
  const [vehicleId,  setVehicleId]  = useState(document?.vehicleId || defaultVehicleId || "");
  const [docType,    setDocType]    = useState(document?.docType || "rc");
  const [docNumber,  setDocNumber]  = useState(document?.docNumber || "");
  const [issueDate,  setIssueDate]  = useState(document?.issueDate ? document.issueDate.slice(0, 10) : "");
  const [expiryDate, setExpiryDate] = useState(document?.expiryDate ? document.expiryDate.slice(0, 10) : "");
  const [notes,      setNotes]      = useState(document?.notes || "");
  const [file,       setFile]       = useState(null);
  const [existingFileName] = useState(document?.fileName || null);
  const [saving,     setSaving]     = useState(false);
  const fileInputRef = useRef(null);

  function handleFilePick(e) {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (!ALLOWED_TYPES.includes(picked.type)) {
      return toast.error("Please choose a PDF, JPG, PNG, or WEBP file");
    }
    if (picked.size > MAX_FILE_SIZE) {
      return toast.error("File is too big — please keep it under 10 MB");
    }
    setFile(picked);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!vehicleId) return toast.error("Select a vehicle");
    setSaving(true);
    try {
      await onSubmit({
        vehicleId, docType,
        docNumber:  docNumber.trim()  || null,
        issueDate:  issueDate  || null,
        expiryDate: expiryDate || null,
        notes:      notes.trim() || null,
        file,
      });
      toast.success(isEdit ? "Document updated" : "Document added");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save document");
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 440,
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
          }}
        >
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              {isEdit ? "Edit Document" : "Add Document"}
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Vehicle *</label>
              <select
                style={{ ...inputStyle, cursor: isEdit ? "default" : "pointer" }}
                value={vehicleId}
                onChange={e => setVehicleId(e.target.value)}
                disabled={isEdit}
              >
                <option value="">— Select vehicle —</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.nickname || v.plateNumber}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Document Type *</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} value={docType} onChange={e => setDocType(e.target.value)}>
                {Object.entries(DOC_TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Document Number</label>
              <input style={inputStyle} value={docNumber} onChange={e => setDocNumber(e.target.value)} placeholder="Optional" />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Issue Date</label>
                <input type="date" style={inputStyle} value={issueDate} onChange={e => setIssueDate(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Expiry Date</label>
                <input type="date" style={inputStyle} value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Photo or PDF of the document</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={handleFilePick}
                style={{ display: "none" }}
              />
              {file ? (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                  border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "8px 12px", background: "#F9FAFB",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <FileText size={16} color="#4F46E5" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {file.name}
                    </span>
                    <span style={{ fontSize: 11.5, color: "#9CA3AF", flexShrink: 0 }}>{formatFileSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 600, flexShrink: 0 }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    height: 42, border: "1.5px dashed #D1D5DB", borderRadius: 8, background: "#F9FAFB",
                    color: "#4F46E5", fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  <Upload size={15} />
                  {existingFileName ? `Replace file (currently: ${existingFileName})` : "Choose file to upload"}
                </button>
              )}
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <input style={inputStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
            </div>

            <button
              type="submit"
              disabled={saving}
              style={{
                marginTop: 6, width: "100%", height: 42, borderRadius: 10, border: "none",
                background: saving ? "#9CA3AF" : "#4F46E5",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {saving ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : isEdit ? "Save Changes" : "Add Document"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
